import moment from 'moment';
import { handleActions } from 'redux-actions';
import http from '../shared/http';
import intl from '../shared/intl';
import endpoints from '../../config/endpoints';
import { create as paginationCreate } from '../shared/pagination';
import createCalendar from '../shared/calendar';
import { LOCATION_CHANGE } from 'react-router-redux';

import { REFETCH_ALL_DATA } from '../root';

import { formValueSelector } from 'redux-form';

import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import findIndex from 'lodash/findIndex';
import capitalize from 'lodash/capitalize';

import { enum as newsTypesEnum } from '../../../../enums/news-types';

const calendar = createCalendar({
	namespace: 'news',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const pagination = paginationCreate('news', {
	NewsList: {
		currentPage: 1,
		perPage: 3
	}
});

// actions
const FETCH_NEWS_SUCCESS = 'news/fetch-data-success';
const FETCH_NEWS_ERROR = 'news/fetch-data-error';

const FETCH_NEWS_OFFERS_SUCCESS = 'news/fetch-news-offers-success';
const FETCH_NEWS_OFFERS_ERROR = 'news/fetch-news-offers-error';

const DISABLE_FILTER_CONTEXT = 'news/disable-filter-context';

const TOGGLE_NEWS = 'news/toggle-news';

const initialState = {
	data: null,
	errors: null,
	calendar: calendar.initialState,
	pagination: pagination.initState,
	filters: {},
	tableId: 1, // for animation table must have uniq key
	openedNewsId: null,
	openedNewsOffers: {},
	disableFilterContext: false
};

// actions
export const actions = Object.assign({
	fetchData: (props) => (dispatch, getState) => {
		const request = http({
			url: [`${endpoints.NEWS.LIST}`],
			method: 'get',
			actions: [FETCH_NEWS_SUCCESS, FETCH_NEWS_ERROR],
			addToResponse: {
				newsId: props.params.newsId
			}
		});

		request(dispatch).then(action => {
			// open news by newsID
			if (action.type === FETCH_NEWS_SUCCESS && props.params.newsId) {
				dispatch(actions.disableFilterContext());

				const data = groupByDate(getTableRows(getState()));
				const toggleNewsIndexes = data.reduce((acc, entity, index) => {
					const newsIndexForOpen = findIndex(entity.news, {id: +props.params.newsId});
					if (newsIndexForOpen !== -1) {
						acc = [].concat([`${index}_${newsIndexForOpen}_news`, props.params.newsId]);
					}
					return acc;
				}, []);

				if (toggleNewsIndexes.length === 2) {
					dispatch(actions.toggleNews(...toggleNewsIndexes));
				}
			}
		});
	},

	toggleNews: (index, id) => (dispatch, getState) => {
		const openedId = getState().news.openedNewsOffers.id;

		if (openedId != id) {
			const request = http({
				url: [`${endpoints.NEWS.LIST}/${id}`],
				method: 'get',
				actions: [FETCH_NEWS_OFFERS_SUCCESS, FETCH_NEWS_OFFERS_ERROR]
			});
			dispatch(request).then(action => {
				dispatch({
					type: TOGGLE_NEWS,
					payload: index
				});
			});
		} else {
			dispatch({
				type: TOGGLE_NEWS,
				payload: index
			});
		}
	},

	disableFilterContext: () => ({
		type: DISABLE_FILTER_CONTEXT
	}),

	pagination: () => dispatch => pagination.actions(dispatch)
},
calendar.actions);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_NEWS_SUCCESS]: (state, action) => {
			const [res] = action.payload.map(res => res.data);
			const newsId = action.meta.newsId;
			const newsEntity = find(res, { id: +newsId });

			const data = newsEntity ? [newsEntity] : res;

			return { ...state, data };
		},
		[FETCH_NEWS_OFFERS_SUCCESS]: (state, action) => {
			const [data] = action.payload.map(res => res.data);
			return { ...state, openedNewsOffers: data };
		},
		[FETCH_NEWS_ERROR]: (state, action) => {
			return { ...state, error: true };
		},
		[TOGGLE_NEWS]: (state, action) => {
			return { ...state, openedNewsId: action.payload === state.openedNewsId ? null : action.payload };
		},
		[DISABLE_FILTER_CONTEXT]: (state, action) => ({
			...state,
			disableFilterContext: true
		}),
		[REFETCH_ALL_DATA]: () => initialState
	},
	calendar.reducers,
	pagination.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.news.data,
	getNewsTypesEnum: () => newsTypesEnum,
	getLocalizedNewsTypes: state =>
		state.app.lists['news-types'].map(item => ({
			id: item.id,
			text: capitalize(item.text)
		})),
	getTableData: state =>
		pagination.selectors.getPageData(
			state => state.news.data && groupByDate(getTableRows(state)),
			state => state.news.pagination.NewsList.perPage,
			state => state.news.pagination.NewsList.currentPage
		)(state),
	getOpenedNewsId: state => state.news.openedNewsId,
	getOpenedNewsOffers: state => state.news.openedNewsOffers.news_offers,
	getCalendar: state => calendar.selectors.getCalendar(state => state.news.calendar, state => state)(state),
	getIntl: state => intl(state),
	getError: state => state.news.error
};

const getTableRows = (state) => {
	const { data, disableFilterContext } = state.news;
	const formSelector = formValueSelector('filtersNewsForm');
	const search = formSelector(state, 'newsSearch');
	const newsType = formSelector(state, ...Object.values(newsTypesEnum));
	const { calendar } = state.news;
	const { start, end } = calendar.value || calendar.defaultValue;

	let textSearch = (search && search.toLowerCase()) || '';

	if (disableFilterContext) return data;

	return data.filter(item => {
		return (
			!newsType[item.type] &&
			item.title &&
			item.text &&
			(item.title.toLowerCase().indexOf(textSearch) >= 0 ||
				item.text.toLowerCase().indexOf(textSearch) >= 0 ||
				(item.offers && find(item.offers, offer => offer.text.toLowerCase().indexOf(textSearch) >= 0))) &&
			moment(start).startOf('day') <= moment(item.published_at) &&
			moment(end).endOf('day') >= moment(item.published_at)
		);
	});
};

const groupByDate = rows => {
	return map(groupBy(rows, row => moment(row.published_at).format('YYYY-MM-DD')), (value, key) => ({
		date: key,
		news: value
	}));
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/news/index.js